<template>
  <header
    class="w-100 bg-primary-dark text-white px-3 d-flex justify-content-between align-items-center shadow-sm fixed-top app-header-safe"
    ref="headerRef"
  >
    <!-- Botón hamburger y volver -->
    <div class="d-flex align-items-center gap-2">
      <button
        v-if="
          shouldShowBackButton && router.currentRoute.value.name !== 'main-view'
        "
        class="nav-back-btn"
        @click="goBack"
        :title="t('header.back')"
      >
        <i class="bi bi-arrow-left"></i>
      </button>
      <button
        class="hamburger-btn"
        @click="drawerOpen = !drawerOpen"
        :title="t('header.menu')"
      >
        <i class="bi bi-list"></i>
      </button>

      <!-- Contexto admin: sin selector de colegio/grupo -->
      <div v-if="isAdminArea" class="admin-header-label">
        {{ t("header.administration") }}
      </div>

      <!-- Botón único para seleccionar grupo -->
      <button
        v-else-if="cursoSeleccionado"
        class="group-selector-btn"
        @click="openGroupSelectorModal"
        :title="t('header.selectGroup')"
      >
        <div class="group-selector-content">
          <div class="group-selector-name">{{ nombreCurso }}</div>
          <div class="group-selector-represented">
            {{ nombreRepresentado }}
          </div>
        </div>
        <i class="bi bi-chevron-down group-selector-icon"></i>
      </button>
      <button
        v-else-if="selectedColegio"
        class="group-selector-btn"
        @click="openGroupSelectorModal"
        :title="t('header.selectGroup')"
      >
        <div class="group-selector-content">
          <div class="group-selector-school">{{ selectedColegio }}</div>
        </div>
        <i class="bi bi-chevron-down group-selector-icon"></i>
      </button>
    </div>

    <!-- Botones del lado derecho -->
    <div class="d-flex align-items-center gap-2 header-actions">
      <!-- Acceso directo a mensajería (reemplaza el antiguo usuario+) -->
      <button
        v-if="showMessagingHeaderButton && !isBottomNavActive"
        class="home-btn-mobile messaging-header-btn"
        type="button"
        :title="t('header.messaging')"
        :aria-label="t('header.openMessaging')"
        @click="openMessagingFromHeader"
      >
        <i class="bi bi-chat-dots"></i>
        <span
          v-if="messagingUnreadBadge"
          class="messaging-badge"
          >{{ messagingUnreadBadge }}</span
        >
        <span
          v-else-if="messagingHasUnreadDot"
          class="messaging-badge messaging-badge--dot"
          aria-hidden="true"
        />
      </button>
      <!-- Botón home para móvil (solo cuando no estamos en main-view) -->
      <!-- Home: en web siempre (útil en desktop). Con bottom nav en app/PWA mobile no hace falta. -->
      <button
        v-if="shouldShowHomeButton && (!isBottomNavActive || shouldShowBackButton)"
        class="home-btn-mobile"
        @click="irAInicio"
        :title="t('header.home')"
      >
        <i class="bi bi-house-fill"></i>
      </button>
    </div>
  </header>

  <!-- Overlay del drawer -->
  <div
    v-if="drawerOpen"
    class="drawer-overlay"
    @click="drawerOpen = false"
  ></div>

  <!-- Drawer lateral personalizado -->
  <div
    class="drawer-container"
    :class="{ 'drawer-open': drawerOpen, 'drawer-container--cpa': isCpaMenuMode }"
  >
    <div class="drawer-content">
      <!-- Encabezado de contexto del grupo activo (CPA y cursos regulares) -->
      <div v-if="cursoSeleccionado" class="drawer-context">
        <div class="drawer-context__school">
          {{ drawerContextSchool }}
        </div>
        <div class="drawer-context__group">{{ drawerContextGroup }}</div>
        <div v-if="drawerContextMeta" class="drawer-context__meta">
          {{ drawerContextMeta }}
        </div>
        <div v-if="drawerContextStudent" class="drawer-context__student">
          {{ drawerContextStudent }}
        </div>
      </div>

      <!-- Opciones del Menú Grupo organizadas por secciones -->
      <div
        v-if="cursoSeleccionado && gruposVisibles.length > 0"
        class="drawer-menu-sections drawer-menu-sections--scroll"
      >
        <template v-for="grupo in gruposVisibles" :key="grupo.nombre">
          <div
            v-if="grupo.separatorBefore"
            class="drawer-menu-separator"
            aria-hidden="true"
          />

          <!-- Enlace directo (Inicio CPA) -->
          <button
            v-if="grupo.isDirectLink"
            type="button"
            class="drawer-menu-item drawer-menu-direct"
            :class="{
              'drawer-menu-item--active': isGrupoDirectoActivo(grupo),
            }"
            @click="seleccionarOpcionMenu(grupo.opciones[0]?.texto)"
          >
            <i
              :class="grupo.icono || getGrupoIcon(grupo)"
              class="drawer-icon"
              :data-tone="grupo.iconTone"
            ></i>
            <span class="drawer-menu-text">{{
              translateDrawerLabel(grupo.nombre)
            }}</span>
          </button>

          <div v-else class="drawer-menu-group">
            <div
              class="drawer-menu-group-header drawer-menu-group-header--clickable"
              role="button"
              tabindex="0"
              :aria-expanded="!estaColapsado(grupo)"
              :aria-controls="`drawer-section-${slugify(grupo.nombre)}`"
              :aria-label="`${estaColapsado(grupo) ? t('header.expand') : t('header.collapse')} ${translateDrawerLabel(grupo.nombre)}`"
              @click="toggleGrupoColapsable(grupo)"
              @keydown.enter.prevent="toggleGrupoColapsable(grupo)"
              @keydown.space.prevent="toggleGrupoColapsable(grupo)"
            >
              <i
                :class="grupo.icono || getGrupoIcon(grupo)"
                class="drawer-group-icon"
                :data-tone="grupo.iconTone"
              ></i>
              <span class="drawer-menu-group-title">{{
                translateDrawerLabel(grupo.nombre)
              }}</span>
              <i
                :class="estaColapsado(grupo) ? 'bi bi-chevron-down' : 'bi bi-chevron-up'"
                class="drawer-collapse-arrow"
                aria-hidden="true"
              ></i>
            </div>
            <div
              :id="`drawer-section-${slugify(grupo.nombre)}`"
              class="drawer-menu-group-items"
              :class="{
                'drawer-menu-group-items--expanded': !estaColapsado(grupo),
              }"
            >
              <button
                v-for="opcion in grupo.opciones"
                :key="opcion.texto"
                type="button"
                class="drawer-menu-item drawer-menu-sub-item"
                :class="{
                  'drawer-menu-item--active': isOpcionMenuActiva(opcion),
                }"
                @click="seleccionarOpcionMenu(opcion.texto)"
              >
                <i
                  :class="opcion.icono || 'bi bi-arrow-right'"
                  class="drawer-icon"
                ></i>
                <span class="drawer-menu-text">{{
                  translateDrawerLabel(opcion.texto)
                }}</span>
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- Bloque inferior: Usuario -->
      <div class="drawer-user-section">
        <div class="drawer-user-info">
          <div class="drawer-avatar">
            <i class="bi bi-person-circle" style="font-size: 36px"></i>
          </div>
          <div class="drawer-user-details">
            <div class="drawer-user-name">
              {{ userFullName || t("header.user") }}
            </div>
            <div class="drawer-user-email">{{ userEmail }}</div>
          </div>
          <div class="drawer-menu-toggle-wrapper" ref="userMenuToggleRef">
            <button 
              v-if="!esRutaInvitacion"
              class="drawer-menu-toggle" 
              @click.stop="toggleUserMenu"
            >
              <i class="bi bi-three-dots-vertical"></i>
            </button>

            <!-- Popover del menú contextual del usuario -->
            <div v-if="showUserMenu && !esRutaInvitacion" class="drawer-user-popover" @click.stop>
              <button
                v-if="canAssociateExtraUser"
                class="drawer-popover-item"
                @click="openApoderadoModal"
              >
                <i class="bi bi-person-plus drawer-popover-icon"></i>
                <span class="drawer-popover-text">{{
                  t("header.additionalGuardians")
                }}</span>
              </button>
              <button class="drawer-popover-item" @click="toggleDarkMode">
                <i
                  :class="isDarkMode ? 'bi bi-sun-fill' : 'bi bi-moon-fill'"
                  class="drawer-popover-icon"
                ></i>
                <span class="drawer-popover-text">
                  {{
                    isDarkMode
                      ? t("header.lightMode")
                      : t("header.darkMode")
                  }}
                </span>
              </button>
              <div class="drawer-popover-item drawer-popover-language">
                <i class="bi bi-translate drawer-popover-icon"></i>
                <span class="drawer-popover-text">{{ t("language.label") }}</span>
                <LanguageSwitcher class="ms-auto" />
              </div>
              <button
                class="drawer-popover-item drawer-popover-logout"
                @click="logout"
              >
                <i class="bi bi-box-arrow-right drawer-popover-icon"></i>
                <span class="drawer-popover-text">{{ t("header.logout") }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal de Apoderado Extra -->
  <ApoderadoExtraModal
    :show="showApoderadoModal"
    @close="closeApoderadoModal"
    @success="handleApoderadoSuccess"
  />

  <EncuestaModal
    v-if="encuestaActual && cursoSeleccionado"
    :show="showEncuestaModal"
    :survey="encuestaActual"
    :school-id="cursoSeleccionado.school_id"
    :year="String(cursoSeleccionado.year || '')"
    :course-code="courseCodeEncuesta"
    :encuesta-index="encuestaModalIndex"
    :total-encuestas="encuestasPendientes.length"
    @close="cerrarEncuestaModal"
    @success="onEncuestaRespondida"
  />
  <SurveyRequiredPrompt
    :show="showRequiredSurveyPrompt"
    :count="surveyStore.requiredPendingCount"
    :survey-title="firstRequiredSurvey?.survey?.title"
    @respond="respondRequiredSurvey"
    @later="snoozeRequiredSurvey"
  />

  <!-- Modal de Usuario Extra -->
  <div
    v-if="showUsuarioExtraModal"
    class="modal fade show"
    style="display: block"
    tabindex="-1"
    @click.self="closeUsuarioExtraModal"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Acceso Restringido</h5>
          <button
            type="button"
            class="btn-close"
            @click="closeUsuarioExtraModal"
          ></button>
        </div>
        <div class="modal-body text-center">
          <i
            class="bi bi-exclamation-triangle text-warning"
            style="font-size: 3rem"
          ></i>
          <h6 class="mt-3">Eres un usuario extra en este curso</h6>
          <p class="text-muted">
            Como usuario extra, no puedes asociar otros usuarios a este grupo.
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-primary"
            @click="closeUsuarioExtraModal"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Sheet de Selección de Grupos -->
  <div
    v-if="showGroupSelectorModal"
    class="group-selector-overlay"
    @click="closeGroupSelectorModal"
  >
    <div class="group-selector-bottom-sheet" @click.stop>
      <div class="group-selector-handle"></div>
      <div class="group-selector-header">
        <div class="group-selector-header-left">
          <h5 class="group-selector-title">Seleccionar Grupo</h5>
          <div
            v-if="añosDisponibles.length > 0"
            class="year-selector-pill"
          >
            <span class="year-selector-label">Año</span>
            <select
              class="year-selector-select"
              v-model="añoActivoLocal"
              @change="handleYearChange"
            >
              <option
                v-for="year in añosDisponibles"
                :key="year"
                :value="year"
              >
                {{ year }}
              </option>
            </select>
          </div>
        </div>
        <button
          type="button"
          class="group-selector-close"
          @click="closeGroupSelectorModal"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="group-selector-body">
        <div v-if="cursosFiltradosPorAño.length === 0" class="group-selector-empty">
          <p>No hay grupos disponibles</p>
        </div>
        <div v-else class="group-list">
          <button
            v-for="curso in cursosFiltradosPorAño"
            :key="curso.key"
            class="group-item"
            :class="{
              'group-item-active': cursoSeleccionado?.key === curso.key,
            }"
            @click="seleccionarGrupo(curso)"
          >
            <div class="group-item-icon">
              <i
                :class="
                  isFuncionariosCourse(curso)
                    ? 'bi bi-person-badge-fill'
                    : curso.entity === 'course'
                      ? 'bi bi-mortarboard-fill'
                      : 'bi bi-building'
                "
              ></i>
            </div>
            <div class="group-item-content">
              <div class="group-item-name">{{ getLocalizedGroupSelectorTitle(curso, t) }}</div>
              <div class="group-item-school">{{ getGroupSelectorSubtitle(curso) }}</div>
              <div
                v-if="
                  courseRequiresStudentName(curso) && curso.student_name
                "
                class="group-item-student"
              >
                <i class="bi bi-person"></i>
                {{ curso.student_name }}
              </div>
            </div>
            <i
              v-if="cursoSeleccionado?.key === curso.key"
              class="bi bi-check-circle-fill group-item-check"
            ></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { getDrawerLabel } from "@/i18n";
import { getLocalizedGroupSelectorTitle } from "@/utils/localizedGroupLabel";
import { useUserStore } from "@/store/userStore";
import { useCursoStore } from "@/store/cursoStore";
import { useColegioStore } from "@/store/colegioStore";
import { useGuardianStore } from "@/store/guardianStore";
import {
  filterCoursesByYear,
  useYearStore,
} from "@/store/yearStore";
import { mapCourseCodeForYearChange } from "@/utils/mapCourseCodeForYearChange";
import { parseCourseCode, isAlumnosCourse, isCpaCourse, isCpaDirectiva, isCpaDelegado, isFuncionariosCourse, isKioskCourse, courseRequiresStudentName, getGroupSelectorTitle, getGroupSelectorSubtitle } from "@/utils/parseCourseCode";
import {
  findGateGuardCourse,
  isGateGuardCourse,
  findInspectorCourse,
  isInspectorCourse,
  isAcademicStaffCourse,
  resolveAppHomeRouteName,
  shouldUseGateGuardHome,
  shouldUseInspectorHome,
} from "@/utils/funcionarios";
import {
  buildCpaDelegateMenu,
  buildCpaMenu,
  findActiveCpaSectionName,
  getCpaRoleLabels,
  isCpaMenuOptionActive,
} from "@/navigation/cpaMenu";
import { buildCourseDrawer } from "@/navigation/courseDrawer";
import { useModuleVisibility } from "@/composables/useModuleVisibility";
import { isPlatformAdmin } from "@/router/guards/platformAdmin";
import { usePWADetection } from "@/composables/usePWADetection";
import { useGuardianContext } from "@/composables/useGuardianContext";
import { useDarkMode } from "@/composables/useDarkMode";
import { Capacitor } from "@capacitor/core";
import ApoderadoExtraModal from "./ApoderadoExtraModal.vue";
import LanguageSwitcher from "./LanguageSwitcher.vue";
import EncuestaModal from "./EncuestaModal.vue";
import SurveyRequiredPrompt from "./surveys/SurveyRequiredPrompt.vue";
import {
  fetchMySurveysForCourse,
  fetchMySurveyResponse,
} from "@/services/surveyService";
import { SURVEY_QUESTION_TEXT_SHORT } from "@/types/survey";
import { clearAllSessionCache } from "@/utils/sessionCacheService";
import { useMessagingStore } from "@/store/messagingStore";
import { useSurveyStore } from "@/store/surveyStore";
import { isSurveyInboxActionablePending } from "@/types/survey";
import {
  readMessagingReturnContext,
  useMessagingNavigation,
} from "@/composables/useMessagingNavigation";
import { useBottomNavShell } from "@/composables/useBottomNavShell";
import {
  resolveAppSection,
  rootRouteNameForSection,
} from "@/navigation/appSections";
import { shouldUseBottomNavShell } from "@/navigation/bottomNavEligibility";

const router = useRouter();
const { locale, t } = useI18n();
const translateDrawerLabel = (label) =>
  getDrawerLabel(String(label || ""), locale.value);
const userStore = useUserStore();
const cursoStore = useCursoStore();
const colegioStore = useColegioStore();
const guardianStore = useGuardianStore();
const yearStore = useYearStore();
const messagingStore = useMessagingStore();
const surveyStore = useSurveyStore();
const { isPWAMobile, shouldShowBackButton } = usePWADetection();
const {
  messagingAvailable,
  openMessaging,
  goBackFromMessaging,
} = useMessagingNavigation();
const { isBottomNavActive } = useBottomNavShell();
const { applyDrawerVisibility, visibleModules, groupSettings } =
  useModuleVisibility();
const { isPrimaryGuardian, isAdditionalGuardian } = useGuardianContext();

const userEmail = computed(() => userStore.email);
const userFullName = computed(() => guardianStore.fullName);
const cursoSeleccionado = computed(() => cursoStore.selectedCursoData);

const esRutaInvitacion = computed(() => {
  const routeName = router.currentRoute.value.name;
  return (
    routeName === "agregar-apoderado-curso" ||
    routeName === "agregar-usuario-grupo"
  );
});

/** Admin de plataforma: sin selector de colegio/grupo arriba */
const isAdminArea = computed(() => {
  const path = String(router.currentRoute.value.path || "");
  return path.startsWith("/admin");
});

const encuestasPendientes = ref([]);
const showEncuestaModal = ref(false);
const encuestaModalIndex = ref(0);
const showRequiredSurveyPrompt = ref(false);
const REQUIRED_SURVEY_SNOOZE_MS = 30 * 60 * 1000;
let requiredSurveyTimer = null;

const firstRequiredSurvey = computed(() =>
  surveyStore.inbox.find(
    (item) => item.required && isSurveyInboxActionablePending(item),
  ),
);

const requiredSurveySnoozeKey = computed(() => {
  const schoolId = cursoSeleccionado.value?.school_id || "school";
  return `requiredSurveySnooze:${userEmail.value || "user"}:${schoolId}`;
});

const isSurveyResponseRoute = () => {
  const routeName = String(router.currentRoute.value.name || "");
  return (
    routeName === "encuestas-pendientes" ||
    routeName === "encuesta-responder"
  );
};

const openRequiredSurveyPromptIfNeeded = () => {
  if (
    surveyStore.requiredPendingCount <= 0 ||
    isSurveyResponseRoute() ||
    esRutaInvitacion.value
  ) {
    showRequiredSurveyPrompt.value = false;
    return;
  }
  const snoozedAt = Number(
    localStorage.getItem(requiredSurveySnoozeKey.value) || 0,
  );
  showRequiredSurveyPrompt.value =
    !snoozedAt || Date.now() - snoozedAt >= REQUIRED_SURVEY_SNOOZE_MS;
};

const scheduleRequiredSurveyPrompt = () => {
  if (requiredSurveyTimer) window.clearTimeout(requiredSurveyTimer);
  requiredSurveyTimer = window.setTimeout(() => {
    openRequiredSurveyPromptIfNeeded();
  }, REQUIRED_SURVEY_SNOOZE_MS);
};

const snoozeRequiredSurvey = () => {
  showRequiredSurveyPrompt.value = false;
  localStorage.setItem(requiredSurveySnoozeKey.value, String(Date.now()));
  scheduleRequiredSurveyPrompt();
};

const respondRequiredSurvey = () => {
  showRequiredSurveyPrompt.value = false;
  router.push({ name: "encuestas-pendientes" });
};

const courseCodeEncuesta = computed(() => {
  const c = cursoSeleccionado.value;
  if (!c) return "";
  return c.course_code || c.course_id || "";
});

const encuestaActual = computed(() => {
  const list = encuestasPendientes.value;
  const i = encuestaModalIndex.value;
  if (!list.length || i < 0 || i >= list.length) return null;
  return list[i];
});

const filtrarEncuestasConTextoCorto = (surveys) =>
  (surveys || []).filter((s) =>
    (s.questions || []).some((q) => q.type === SURVEY_QUESTION_TEXT_SHORT)
  );

/** true si el usuario ya tiene respuesta guardada para esa encuesta */
const usuarioYaRespondioEncuesta = async (surveyId) => {
  try {
    const r = await fetchMySurveyResponse(surveyId);
    return Boolean(r?.answers?.length);
  } catch {
    return false;
  }
};

/** Índice de la primera encuesta filtrada sin respuesta, o -1 si todas respondidas */
const indicePrimeraEncuestaSinResponder = async (filtradas) => {
  if (!filtradas.length) return -1;
  const flags = await Promise.all(
    filtradas.map((s) => usuarioYaRespondioEncuesta(s.surveyId))
  );
  return flags.findIndex((respondio) => !respondio);
};

const clampEncuestaModalIndex = (i, filtradasLength) => {
  if (filtradasLength <= 0) return 0;
  return Math.max(0, Math.min(i, filtradasLength - 1));
};

const cargarEncuestas = async (options = {}) => {
  const preserveModal = options.preserveModal === true;
  const mantenerModalTrasGuardar = options.mantenerModalTrasGuardar === true;
  const curso = cursoSeleccionado.value;

  if (!curso || esRutaInvitacion.value) {
    encuestasPendientes.value = [];
    encuestaModalIndex.value = 0;
    showEncuestaModal.value = false;
    surveyStore.clear();
    showRequiredSurveyPrompt.value = false;
    return;
  }

  if (!localStorage.getItem("authToken")) {
    encuestasPendientes.value = [];
    return;
  }

  const courseCode = curso.course_code || curso.course_id || "";
  if (!courseCode) return;

  await surveyStore.loadInbox(curso.school_id);
  openRequiredSurveyPromptIfNeeded();

  // Si hay bandeja moderna, no auto-abrir el modal legacy TEXT_SHORT
  if (surveyStore.pendingCount > 0) {
    encuestasPendientes.value = [];
    showEncuestaModal.value = false;
    return;
  }

  if (!preserveModal) {
    encuestasPendientes.value = [];
    encuestaModalIndex.value = 0;
    showEncuestaModal.value = false;
  }

  try {
    const surveys = await fetchMySurveysForCourse({
      school_id: curso.school_id,
      year: String(curso.year),
      course_code: courseCode,
    });
    const filtradas = filtrarEncuestasConTextoCorto(surveys);
    encuestasPendientes.value = filtradas;

    if (filtradas.length === 0) {
      showEncuestaModal.value = false;
      encuestaModalIndex.value = 0;
    } else {
      const idx = await indicePrimeraEncuestaSinResponder(filtradas);
      if (idx !== -1) {
        encuestaModalIndex.value = idx;
        showEncuestaModal.value = true;
      } else if (mantenerModalTrasGuardar) {
        encuestaModalIndex.value = clampEncuestaModalIndex(
          encuestaModalIndex.value,
          filtradas.length
        );
        showEncuestaModal.value = true;
      } else {
        encuestaModalIndex.value = 0;
        showEncuestaModal.value = false;
      }
    }
  } catch (e) {
    console.warn("No se pudieron cargar encuestas:", e);
  }
};

const cerrarEncuestaModal = () => {
  showEncuestaModal.value = false;
};

const onEncuestaRespondida = async () => {
  await cargarEncuestas({
    preserveModal: true,
    mantenerModalTrasGuardar: true,
  });
};

watch(
  [cursoSeleccionado, esRutaInvitacion],
  () => {
    cargarEncuestas();
  },
  { immediate: true }
);

watch(
  () => router.currentRoute.value.name,
  () => openRequiredSurveyPromptIfNeeded(),
);

const añoActivo = computed(() => yearStore.añoActivo);
const añosDisponibles = computed(() => yearStore.añosDisponibles || []);

// Estado local para el selector (para no depender directamente del getter)
// Nota: este archivo usa lang=\"js\", por eso no usamos tipos de TypeScript aquí.
const añoActivoLocal = ref(null);

watch(
  añoActivo,
  (nuevo) => {
    añoActivoLocal.value = nuevo ?? null;
  },
  { immediate: true }
);

const cursosDisponibles = computed(() => {
  // Cursos base a partir del guardianStore
  const cursos = guardianStore.courses || [];
  return cursos;
});

const cursosFiltradosPorAño = computed(() => {
  const cursos = cursosDisponibles.value;
  // Nunca listar todos los años: si falta año activo, usar el del curso
  // seleccionado o el más reciente disponible.
  const year =
    añoActivo.value ||
    cursoStore.getCursoData()?.year ||
    yearStore.añosDisponibles[0] ||
    null;
  if (!year) {
    // Último recurso: año más alto de los cursos (sin mutar el store aquí).
    const years = Array.from(
      new Set(cursos.map((c) => String(c.year ?? "").trim()).filter(Boolean)),
    ).sort((a, b) => b.localeCompare(a));
    if (!years.length) return [];
    return filterCoursesByYear(cursos, years[0]);
  }
  return filterCoursesByYear(cursos, year);
});

const handleYearChange = () => {
  const nuevoAño = añoActivoLocal.value;
  const añoAnterior = añoActivo.value;
  if (!nuevoAño) {
    return;
  }

  yearStore.setAñoActivo(nuevoAño);

  const cursoActual = cursoStore.getCursoData();
  const cursosDelNuevoAño = filterCoursesByYear(
    cursosDisponibles.value,
    nuevoAño,
  );

  if (cursosDelNuevoAño.length === 0) {
    return;
  }

  // 1) Intentar mantener mismo curso por school_id + course_id
  if (cursoActual) {
    const mismoCurso = cursosDelNuevoAño.find(
      (c) =>
        c.school_id === cursoActual.school_id &&
        c.course_id === cursoActual.course_id
    );

    if (mismoCurso) {
      cursoStore.setCurso(mismoCurso);
      return;
    }
  }

  // 2) Intentar encontrar curso equivalente usando la tabla de equivalencias
  if (cursoActual && añoAnterior) {
    const mappedCode = mapCourseCodeForYearChange(
      cursoActual.course_id,
      cursoActual.school_id,
      añoAnterior,
      nuevoAño
    );

    if (mappedCode) {
      const cursoEquivalente = cursosDelNuevoAño.find(
        (c) =>
          c.school_id === cursoActual.school_id &&
          c.course_id === mappedCode
      );

      if (cursoEquivalente) {
        cursoStore.setCurso(cursoEquivalente);
        return;
      }
    }
  }

  // 3) Si no existe equivalente, usar el primer curso del nuevo año
  cursoStore.setCurso(cursosDelNuevoAño[0]);
};
const selectedColegio = computed(
  () => cursoStore.selectedCursoData?.school_name
);
// Drawer
const drawerOpen = ref(false);
const showUserMenu = ref(false);
const gruposVisibles = ref([]);
const userMenuToggleRef = ref(null);

// Drawer: todos los supergrupos del menú son colapsables
const colapsadoPorGrupo = ref({});

/** Todos los grupos con submenú inician colapsados (cualquier tipo de usuario). */
const grupoColapsadoPorDefecto = () => true;

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const isCpaMenuMode = computed(
  () =>
    isCpaDirectiva(cursoSeleccionado.value) ||
    isCpaDelegado(cursoSeleccionado.value),
);

const cpaRoleLabel = computed(() =>
  getCpaRoleLabels(cursoSeleccionado.value?.roles || []),
);

const COURSE_ROLE_LABELS = {
  president: "Presidente",
  vicepresident: "Vicepresidente",
  treasurer: "Tesorero",
  secretary: "Secretario",
  director: "Director",
};

const COURSE_ROLE_PRIORITY = [
  "treasurer",
  "president",
  "vicepresident",
  "secretary",
  "director",
];

const drawerContextSchool = computed(
  () => cursoSeleccionado.value?.school_name || "Colegio",
);

const drawerContextGroup = computed(() => {
  const curso = cursoSeleccionado.value;
  if (!curso) return "";
  if (isCpaCourse(curso)) return "Centro General de Padres";
  if (isFuncionariosCourse(curso)) return "Funcionarios";
  if (isKioskCourse(curso)) return "Usuarios de kiosco";
  if (curso.course_id?.startsWith("G")) {
    return curso.course_name || curso.course_id || "Grupo";
  }
  const code = String(curso.course_code || curso.course_id || "").trim();
  if (!code) return curso.course_name || "Grupo";
  try {
    return parseCourseCode(code).longName || curso.course_name || code;
  } catch {
    return curso.course_name || code;
  }
});

const drawerContextRole = computed(() => {
  const curso = cursoSeleccionado.value;
  if (!curso) return "";
  if (isCpaMenuMode.value) return cpaRoleLabel.value;
  if (isFuncionariosCourse(curso) || isKioskCourse(curso)) return "";
  const roles = curso.roles || [];
  const labels = COURSE_ROLE_PRIORITY.filter((role) => roles.includes(role)).map(
    (role) => COURSE_ROLE_LABELS[role] || role,
  );
  if (labels.length) return labels.slice(0, 2).join(" · ");
  return "Apoderado";
});

const drawerContextMeta = computed(() => {
  const parts = [];
  if (drawerContextRole.value) parts.push(drawerContextRole.value);
  if (cursoSeleccionado.value?.year) {
    parts.push(`Año ${cursoSeleccionado.value.year}`);
  }
  return parts.join(" · ");
});

/** Pupilo solo cuando la configuración del grupo lo requiere. */
const drawerContextStudent = computed(() => {
  const curso = cursoSeleccionado.value;
  if (!curso) return "";
  if (!courseRequiresStudentName(curso)) return "";
  const student = String(curso.student_name || "").trim();
  return student ? `Pupilo: ${student}` : "";
});

const currentRouteName = computed(() =>
  String(router.currentRoute.value.name || ""),
);
const currentRoutePath = computed(() =>
  String(router.currentRoute.value.path || ""),
);

const isOpcionMenuActiva = (opcion) => {
  if (!isCpaMenuMode.value || !opcion) return false;
  return isCpaMenuOptionActive(
    opcion,
    currentRouteName.value,
    currentRoutePath.value,
  );
};

const isGrupoDirectoActivo = (grupo) => {
  const op = grupo?.opciones?.[0];
  return op ? isOpcionMenuActiva(op) : false;
};

const syncCpaAccordionWithRoute = () => {
  if (!isCpaMenuMode.value) return;
  const active = findActiveCpaSectionName(
    gruposVisibles.value,
    currentRouteName.value,
    currentRoutePath.value,
  );
  if (!active) return;
  for (const grupo of gruposVisibles.value) {
    if (grupo.isDirectLink) continue;
    colapsadoPorGrupo.value[grupo.nombre] = grupo.nombre !== active;
  }
};

const estaColapsado = (grupo) =>
  colapsadoPorGrupo.value[grupo.nombre] ??
  grupoColapsadoPorDefecto(grupo.nombre);

const toggleGrupoColapsable = (grupo) => {
  const actual =
    colapsadoPorGrupo.value[grupo.nombre] ??
    grupoColapsadoPorDefecto(grupo.nombre);
  const expanding = actual;
  colapsadoPorGrupo.value[grupo.nombre] = !actual;
  if (!expanding) return;
  // Tras expandir, asegurar que las opciones queden visibles en el área scroll
  requestAnimationFrame(() => {
    const section = document.getElementById(
      `drawer-section-${slugify(grupo.nombre)}`,
    );
    section?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  });
};

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value;
};

// Cerrar el menú al hacer click fuera
const handleClickOutside = (event) => {
  if (
    showUserMenu.value &&
    userMenuToggleRef.value &&
    !userMenuToggleRef.value.contains(event.target)
  ) {
    showUserMenu.value = false;
  }
};

// Nota: Los event listeners se configuran en el onMounted más abajo

const obtenerGruposVisibles = () => {
  if (!cursoSeleccionado.value) {
    gruposVisibles.value = [];
    return;
  }

  const cursoData = cursoSeleccionado.value;
  const roles = cursoData?.roles || [];
  const tipoTesoreria = cursoData?.treasury_type || "";

  if (isCpaDirectiva(cursoData)) {
    gruposVisibles.value = applyDrawerVisibility(
      buildCpaMenu({
        roles,
        treasuryType: tipoTesoreria,
        schoolName: cursoData.school_name,
        year: cursoData.year,
      }),
    );
    for (const grupo of gruposVisibles.value) {
      if (colapsadoPorGrupo.value[grupo.nombre] === undefined) {
        colapsadoPorGrupo.value[grupo.nombre] = grupoColapsadoPorDefecto(
          grupo.nombre,
        );
      }
    }
    syncCpaAccordionWithRoute();
    return;
  }

  if (isCpaDelegado(cursoData)) {
    gruposVisibles.value = buildCpaDelegateMenu({
      roles,
      treasuryType: tipoTesoreria,
      schoolName: cursoData.school_name,
      year: cursoData.year,
    });
    for (const grupo of gruposVisibles.value) {
      if (colapsadoPorGrupo.value[grupo.nombre] === undefined) {
        colapsadoPorGrupo.value[grupo.nombre] = grupoColapsadoPorDefecto();
      }
    }
    return;
  }

  gruposVisibles.value = applyDrawerVisibility(
    buildCourseDrawer({
      roles,
      treasuryType: tipoTesoreria,
      cursoData,
      bottomNavActive: isBottomNavActive.value,
      isAndroid: Capacitor.getPlatform() === "android",
    }),
  );

  for (const grupo of gruposVisibles.value) {
    if (colapsadoPorGrupo.value[grupo.nombre] === undefined) {
      colapsadoPorGrupo.value[grupo.nombre] = grupoColapsadoPorDefecto(
        grupo.nombre,
      );
    }
  }
};

// Función para obtener el icono del grupo
const getGrupoIcon = (grupo) => {
  switch (grupo.nombre) {
    case "Mis pagos":
      return "bi bi-wallet";
    case "Grupo":
      return "bi bi-mortarboard";
    case "Actividades":
    case "Actividades y servicios":
      return "bi bi-calendar-event";
    case "Administrativo: Tesorero":
      return "bi bi-cash-stack";
    case "Administrativo: Presidente":
      return "bi bi-person-badge";
    case "Administrativo: Secretario":
      return "bi bi-person-badge";
    case "Extras":
      return "bi bi-stars";
    case "CPA":
      return "bi bi-diagram-3";
    case "Retiros escolares":
      return "bi bi-door-open";
    case "Resumen":
      return "bi bi-house-door";
    case "Inicio CPA":
      return "bi bi-speedometer2";
    case "Tesorería":
      return "bi bi-wallet2";
    case "Organización":
      return "bi bi-people";
    case "Comunidad":
      return "bi bi-heart";
    case "Comunicaciones":
      return "bi bi-megaphone";
    case "Directiva":
      return "bi bi-diagram-3";
    case "Mi cuenta":
      return "bi bi-person";
    default:
      return "bi bi-list";
  }
};

// Función para seleccionar una opción del menú
const seleccionarOpcionMenu = (textoOpcion) => {
  // Buscar la opción en los grupos visibles para obtener su ruta base
  let rutaBase = null;

  for (const grupo of gruposVisibles.value) {
    const opcion = grupo.opciones.find((op) => op.texto === textoOpcion);
    if (opcion && opcion.ruta) {
      rutaBase = opcion.ruta;
      break;
    }
  }

  if (!rutaBase) return;

  const cursoData = cursoSeleccionado.value;
  if (
    (textoOpcion === "Torneos" ||
      textoOpcion === "Ferias" ||
      textoOpcion === "Convivencia escolar" ||
      textoOpcion === "Delegados deportivos" ||
      textoOpcion === "Calendario") &&
    !isCpaDirectiva(cursoData)
  ) {
    return;
  }

  // Ajustar ruta según tipo de tesorería para algunas opciones
  let rutaFinal = rutaBase;
  const tipoTesoreria = cursoData?.treasury_type || "";

  if (textoOpcion === "Registrar pagos" || textoOpcion === "Registrar pago") {
    if (tipoTesoreria === "centro_costos") {
      rutaFinal = "registrar-abono-centro-costo";
    } else if (tipoTesoreria === "cuotas") {
      rutaFinal = "abonar-cobros-cuotas";
    } else {
      rutaFinal = "abonar-cobros-cuotas";
      console.warn(
        "Tipo de tesorería no reconocido para 'Registrar pago(s)', usando ruta por defecto:",
        tipoTesoreria
      );
    }
  } else if (textoOpcion === "Ver mis pagos" || textoOpcion === "Mis pagos") {
    rutaFinal = "mis-pagos";
  }

  drawerOpen.value = false;
  if (rutaFinal === "messaging-conversations") {
    openMessaging({ source: "menu" });
    return;
  }
  router.push({ name: rutaFinal });
};

// Watcher para actualizar grupos cuando cambia el curso seleccionado
watch(
  cursoSeleccionado,
  () => {
    colapsadoPorGrupo.value = {};
    obtenerGruposVisibles();
  },
  { immediate: true }
);

// Al entrar/salir de bottom nav (cambio de perfil/comunidad) regenerar drawer.
watch(isBottomNavActive, () => {
  obtenerGruposVisibles();
});

watch(
  [visibleModules, groupSettings],
  () => {
    obtenerGruposVisibles();
  },
  { deep: true },
);

watch(
  () => [router.currentRoute.value.name, router.currentRoute.value.path],
  () => {
    syncCpaAccordionWithRoute();
  },
);

const nombreCurso = computed(() =>
  getLocalizedGroupSelectorTitle(cursoSeleccionado.value, t),
);

const nombreRepresentado = computed(() =>
  getGroupSelectorSubtitle(cursoSeleccionado.value),
);

const mostrarInfoColegio = computed(() => {
  return (
    selectedColegio.value &&
    (!cursoSeleccionado.value ||
      router.currentRoute.value.name === "seleccion-grupo")
  );
});

const mostrarInfoCurso = computed(() => {
  return cursoSeleccionado.value;
});

const canAssociateExtraUser = computed(() => isPrimaryGuardian.value);

const isMessagingRoute = computed(() => {
  const name = router.currentRoute.value.name;
  return (
    name === "messaging-conversations" || name === "messaging-conversation"
  );
});

const showMessagingHeaderButton = computed(
  () =>
    messagingAvailable.value &&
    !esRutaInvitacion.value &&
    !isMessagingRoute.value,
);

const messagingUnreadBadge = computed(() => messagingStore.unreadBadgeLabel);
const messagingHasUnreadDot = computed(
  () =>
    !messagingStore.unreadBadgeLabel && messagingStore.hasUnreadConversations,
);

const openMessagingFromHeader = () => {
  openMessaging({ source: "header" });
};

// Navegación
const goBack = () => {
  const routeName = router.currentRoute.value.name;
  if (routeName === "messaging-conversations") {
    goBackFromMessaging();
    return;
  }
  if (routeName === "messaging-conversation") {
    const ctx = readMessagingReturnContext();
    if (ctx?.source === "push") {
      goBackFromMessaging();
      return;
    }
    void router.push({ name: "messaging-conversations" });
    return;
  }
  if (window.history.length > 1) {
    router.back();
    return;
  }
  void router.push({ name: "main-view" });
};

const { isDarkMode, initializeDarkMode, toggleDarkMode } = useDarkMode();

// Logout
const logout = async () => {
  await messagingStore.shutdownMessaging();
  // Limpiar todo el caché de sesión usando la función centralizada
  clearAllSessionCache();
  
  // Limpiar también sessionStorage completamente (por si hay otros datos)
  sessionStorage.clear();
  
  // Limpiar datos específicos de colegio
  colegioStore.clearColegio();

  // Si estamos en una plataforma nativa (Android/iOS), navegar a login dentro de la app
  if (Capacitor.isNativePlatform()) {
    router.replace({ name: "login" });
  } else {
    // En web/PWA, redirigir a la URL de logout externa
    const domain = "auth.microcentrum.cl";
    const clientId = "791svclqjelbhnk01eucrjg7b1";
    const logoutRedirectUri = encodeURIComponent(
      "https://app.microcentrum.cl/"
    );
    const logoutUrl = `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutRedirectUri}`;
    window.location.href = logoutUrl;
  }
};

// Cerrar menú de usuario cuando se cierra el drawer
watch(drawerOpen, (newVal) => {
  if (!newVal) {
    showUserMenu.value = false;
  }
});

// 🔍 Altura dinámica del header
const headerRef = ref(null);
const headerHeight = ref(56);

// Ancho de ventana reactivo para detectar móvil
const windowWidth = ref(window.innerWidth);

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth;
};

const syncAppHeaderHeight = () => {
  const el = headerRef.value;
  if (!el) return;
  const styles = window.getComputedStyle(el);
  const paddingTop = parseFloat(styles.paddingTop) || 0;
  const paddingBottom = parseFloat(styles.paddingBottom) || 0;
  // Semántica: --app-header-height = fila de contenido, sin safe-area.
  const contentHeight = Math.round(
    el.offsetHeight - paddingTop - paddingBottom,
  );
  const next = contentHeight > 0 ? contentHeight : 56;
  headerHeight.value = next;
  document.documentElement.style.setProperty("--app-header-height", `${next}px`);
};

onMounted(() => {
  initializeDarkMode();

  syncAppHeaderHeight();

  yearStore.ensureAñoActivo(
    guardianStore.courses || [],
    cursoStore.getCursoData()?.year,
  );

  // Agregar listeners
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", updateWindowWidth);
});

watch(
  () => guardianStore.courses,
  (courses) => {
    if (courses?.length) {
      yearStore.ensureAñoActivo(courses, cursoStore.getCursoData()?.year);
    }
  },
);

onUnmounted(() => {
  // Remover listeners
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", updateWindowWidth);
  if (requiredSurveyTimer) window.clearTimeout(requiredSurveyTimer);
});

const homeRouteName = computed(() => {
  if (isPlatformAdmin()) return "admin-home";
  const activeCourse = cursoStore.getCursoData();
  if (isAlumnosCourse(activeCourse) || isAcademicStaffCourse(activeCourse)) {
    return "asignaciones-home";
  }
  return resolveAppHomeRouteName(guardianStore.courses || []);
});

const irAInicio = () => {
  const name = homeRouteName.value;
  if (name === "validar-retiro") {
    const cursoGuardia = findGateGuardCourse(guardianStore.courses || []);
    if (cursoGuardia) cursoStore.setCurso(cursoGuardia);
  } else if (name === "validar-ingreso" || name === "tablero-retiros") {
    const cursoInspector = findInspectorCourse(guardianStore.courses || []);
    if (cursoInspector) cursoStore.setCurso(cursoInspector);
  }
  router.push({ name });
};

const irACursos = () => {
  irAInicio();
};

// Computed para mostrar el botón home:
// siempre que NO estemos en el home del usuario (main o validar-retiro para guardia)
const shouldShowHomeButton = computed(() => {
  return router.currentRoute.value.name !== homeRouteName.value;
});

// Modal de selección de grupos
const showGroupSelectorModal = ref(false);

const openGroupSelectorModal = () => {
  // El año se inicializaba solo en MainView; con bottom nav se puede abrir
  // el selector sin pasar por ahí y entonces se listaban todos los cursos.
  yearStore.ensureAñoActivo(
    guardianStore.courses || [],
    cursoStore.getCursoData()?.year,
  );
  showGroupSelectorModal.value = true;
};

const closeGroupSelectorModal = () => {
  showGroupSelectorModal.value = false;
};

const seleccionarGrupo = (curso) => {
  const previousSection = resolveAppSection(router.currentRoute.value);
  cursoStore.setCurso(curso);
  if (curso?.year != null && curso.year !== "") {
    yearStore.setAñoActivo(String(curso.year));
  }
  closeGroupSelectorModal();
  const courses = guardianStore.courses || [];
  if (shouldUseGateGuardHome(courses) && isGateGuardCourse(curso)) {
    router.push({ name: "validar-retiro" });
    return;
  }
  if (shouldUseInspectorHome(courses) && isInspectorCourse(curso)) {
    router.push({ name: "validar-ingreso" });
    return;
  }
  if (isAcademicStaffCourse(curso)) {
    router.push({ name: "asignaciones-home" });
    return;
  }

  // Bottom nav: mantener el área actual si el nuevo contexto es elegible.
  if (shouldUseBottomNavShell(curso, userStore.email)) {
    const section = previousSection || "treasury";
    const root = rootRouteNameForSection(section, curso);
    if (router.currentRoute.value.name !== root) {
      router.push({ name: root });
    }
    return;
  }

  // Legacy / perfiles no migrados: volver a main-view
  if (router.currentRoute.value.name !== "main-view") {
    router.push({ name: "main-view" });
  }
};

// Modal de Apoderado Extra
const showApoderadoModal = ref(false);

const openApoderadoModal = () => {
  drawerOpen.value = false;
  showUserMenu.value = false;
  if (isAdditionalGuardian.value) return;
  showApoderadoModal.value = true;
};

const closeApoderadoModal = () => {
  showApoderadoModal.value = false;
};

const handleApoderadoSuccess = () => {
  // Aquí puedes agregar lógica adicional cuando se agrega exitosamente un apoderado
  console.log("Apoderado agregado exitosamente");
};

// Modal de Usuario Extra
const showUsuarioExtraModal = ref(false);

const closeUsuarioExtraModal = () => {
  showUsuarioExtraModal.value = false;
};

</script>

<style scoped>
/* Header */
.logo {
  height: 30px;
  width: auto;
  min-width: 30px;
  transition: all 0.3s ease;
}

header {
  box-sizing: border-box;
  height: calc(
    var(--app-header-height, 56px) + var(--safe-area-inset-top, 0px)
  );
  padding-top: var(--safe-area-inset-top, 0px);
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  background-color: var(--mc-bg-header) !important;
  z-index: 2000;
}

.header-actions {
  flex-shrink: 0;
  gap: 8px !important;
}

.nav-back-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  font-size: 20px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  margin-right: 2px;
}

.nav-back-btn:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateX(-2px);
}

.hamburger-btn {
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 24px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
}

.hamburger-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Botón home para móvil */
.home-btn-mobile {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  font-size: 20px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  flex-shrink: 0;
}

.home-btn-mobile:hover {
  background-color: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.5);
  transform: scale(1.05);
}

/* Botón de selección de grupo */
.admin-header-label {
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  color: #ffffff;
  opacity: 0.95;
  white-space: nowrap;
}

.group-selector-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: #ffffff;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: min(220px, calc(100vw - 168px));
  min-width: 0;
  flex: 1;
}

@media (max-width: 380px) {
  .group-selector-btn {
    max-width: calc(100vw - 156px);
    padding: 6px 10px;
    gap: 6px;
  }
}

.group-selector-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.group-selector-content {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.group-selector-name {
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.group-selector-school {
  font-size: 0.75rem;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  margin-top: 2px;
}

.group-selector-represented {
  font-size: 0.75rem;
  opacity: 0.9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  margin-top: 2px;
}

.group-selector-icon {
  font-size: 0.8rem;
  opacity: 0.8;
  flex-shrink: 0;
}

.curso-info {
  max-width: calc(100vw - 150px);
  overflow: hidden;
  opacity: 1;
  transition: all 0.3s ease;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  padding-left: 0.75rem;
  margin-left: 0.5rem;
}

.curso-nombre {
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  color: #ffffff;
  transition: all 0.2s ease;
}

.colegio-nombre {
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  color: #aaaaaa;
  transition: all 0.2s ease;
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  opacity: 1;
  color: #ffffff;
}

/* Drawer - Paleta azul profesional */
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2999;
  animation: fadeIn 0.3s ease;
}

.drawer-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 90%;
  max-width: 320px;
  height: 100%;
  padding-top: var(--safe-area-inset-top);
  box-sizing: border-box;
  background: var(--mc-bg-header);
  color: #ffffff;
  z-index: 3000;
  transform: translateX(-100%);
  transition: transform 0.3s ease;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

/* Estilos para hacer el scroll visible */
.drawer-container::-webkit-scrollbar {
  width: 8px;
}

.drawer-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.drawer-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
}

.drawer-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

@media (min-width: 768px) {
  .drawer-container {
    width: 320px;
  }

  .drawer-container--cpa {
    width: 340px;
  }
}

.drawer-container.drawer-open {
  transform: translateX(0);
}

.drawer-context {
  position: sticky;
  top: 0;
  z-index: 2;
  flex-shrink: 0;
  padding: 14px 14px 12px;
  background: rgba(15, 40, 90, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
}

.drawer-context__school {
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
  color: #fff;
}

.drawer-context__group {
  margin-top: 2px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.85);
}

.drawer-context__meta {
  margin-top: 6px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.7);
}

.drawer-context__student {
  margin-top: 4px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.65);
}

.drawer-menu-sections--scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  min-height: 0;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;
}

.drawer-menu-separator {
  height: 1px;
  margin: 10px 12px 8px;
  background: rgba(255, 255, 255, 0.22);
}

.drawer-menu-direct {
  margin: 4px 6px 6px;
  font-weight: 600;
}

.drawer-menu-item--active {
  background: rgba(255, 255, 255, 0.18) !important;
  font-weight: 600;
  box-shadow: inset 3px 0 0 #254a75;
}

.drawer-menu-item--active .drawer-icon,
.drawer-menu-item--active .drawer-menu-text {
  color: #fff;
}

.drawer-menu-item:hover:not(.drawer-menu-item--active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.drawer-group-icon[data-tone="green"],
.drawer-icon[data-tone="green"] {
  color: #86efac;
}
.drawer-group-icon[data-tone="indigo"],
.drawer-icon[data-tone="indigo"] {
  color: #254a75;
}
.drawer-group-icon[data-tone="violet"],
.drawer-icon[data-tone="violet"] {
  color: #d8b4fe;
}
.drawer-group-icon[data-tone="amber"],
.drawer-icon[data-tone="amber"] {
  color: #fcd34d;
}
.drawer-group-icon[data-tone="blue"],
.drawer-icon[data-tone="blue"] {
  color: #254a75;
}
.drawer-group-icon[data-tone="gray"],
.drawer-icon[data-tone="gray"] {
  color: #d1d5db;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.drawer-content {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  padding: 0;
  overflow: hidden;
}

.drawer-section {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Secciones del menú grupo */
.drawer-menu-sections {
  padding: 8px 0 2px;
  flex: 1 1 auto;
  min-height: 0;
}

.drawer-menu-group {
  margin-bottom: 4px;
}

.drawer-menu-group-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.drawer-menu-group-header--clickable {
  cursor: pointer;
  user-select: none;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}

.drawer-menu-group-header--clickable:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.drawer-group-icon {
  font-size: 12px;
  opacity: 0.7;
}

.drawer-menu-group-title {
  flex: 1;
}

.drawer-collapse-arrow {
  margin-left: auto;
  font-size: 0.85rem;
  opacity: 0.95;
}

.drawer-menu-group-items {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0 6px;
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  pointer-events: none;
  /* Tope alto: el scroll real lo hace .drawer-menu-sections--scroll */
  transition: max-height 280ms ease, opacity 180ms ease;
}

.drawer-menu-group-items--expanded {
  max-height: 2000px;
  opacity: 1;
  pointer-events: auto;
}

.drawer-menu-sub-item {
  padding: 6px 10px;
  font-size: 0.85rem;
}

.drawer-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 0.875rem;
  border-radius: 6px;
  margin-bottom: 1px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-align: left;
}

.drawer-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.drawer-icon {
  margin-right: 12px;
  color: #ffffff;
}

.drawer-menu-text {
  color: #ffffff;
  flex: 1;
  text-align: left;
}

/* Sección de usuario */
.drawer-user-section {
  flex-shrink: 0;
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  padding: 10px 12px;
  background-color: rgba(15, 40, 90, 0.55);
}

.drawer-user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.drawer-avatar {
  color: #ffffff;
  flex-shrink: 0;
}

.drawer-user-details {
  flex: 1;
  min-width: 0;
}

.drawer-user-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.drawer-user-email {
  font-size: 0.8rem;
  color: #aaaaaa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-menu-toggle-wrapper {
  position: relative;
  flex-shrink: 0;
}

.drawer-menu-toggle {
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.drawer-menu-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Popover del menú contextual */
.drawer-user-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: var(--mc-bg-card);
  border: 1px solid var(--mc-border-primary);
  border-radius: 8px;
  box-shadow: var(--mc-shadow-md);
  min-width: 200px;
  padding: 4px;
  z-index: 3100;
  animation: slideUpPopover 0.2s ease;
}

@keyframes slideUpPopover {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.drawer-popover-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: var(--mc-text-primary);
  font-size: 0.875rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  text-align: left;
}

.drawer-popover-item:hover {
  background-color: var(--mc-bg-tertiary);
}

.drawer-popover-icon {
  color: var(--mc-text-secondary);
  font-size: 16px;
  flex-shrink: 0;
}

.drawer-popover-text {
  color: var(--mc-text-primary);
  flex: 1;
  text-align: left;
}

.drawer-popover-language {
  cursor: default;
}

.drawer-popover-language:hover {
  background-color: transparent;
}

.drawer-popover-logout {
  color: var(--mc-danger) !important;
}

.drawer-popover-logout .drawer-popover-text {
  color: var(--mc-danger) !important;
}

.drawer-popover-logout .drawer-popover-icon {
  color: var(--mc-danger) !important;
}

.drawer-popover-logout:hover {
  background-color: rgba(231, 76, 60, 0.15) !important;
}

/* Responsive */
@media (min-width: 768px) {
  .logo {
    height: 35px;
    min-width: 35px;
  }

  .curso-info {
    max-width: 300px;
  }

  .curso-nombre {
    font-size: 0.95rem;
  }

  .colegio-nombre {
    font-size: 0.8rem;
  }
}

/* Animaciones */
.curso-info-enter-active,
.curso-info-leave-active {
  transition: all 0.3s ease;
}

.curso-info-enter-from,
.curso-info-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

/* Estilos para el modal de usuario extra */
.modal-success .modal-content {
  border: none;
  border-radius: 12px;
}

.modal-success .success-icon {
  font-size: 4rem;
  color: #28a745;
}

/* Modo oscuro para el modal */
:root[data-bs-theme="dark"] .modal-content {
  background-color: var(--mc-text-primary);
  border-color: rgba(255, 255, 255, 0.15);
}

:root[data-bs-theme="dark"] .modal-header {
  border-color: rgba(255, 255, 255, 0.15);
}

:root[data-bs-theme="dark"] .modal-footer {
  border-color: rgba(255, 255, 255, 0.15);
}

/* Bottom Sheet de selección de grupos */
.group-selector-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 4000;
  display: flex;
  align-items: flex-end;
  animation: fadeIn 0.3s ease;
}

.group-selector-bottom-sheet {
  width: 100%;
  max-height: 80vh;
  background: var(--mc-bg-card);
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease;
  box-shadow: var(--mc-shadow-lg);
}

.group-selector-handle {
  width: 40px;
  height: 4px;
  background: var(--mc-border-secondary);
  border-radius: 2px;
  margin: 12px auto 8px;
}

.group-selector-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--mc-border-primary);
}

.group-selector-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--mc-text-primary);
}

.group-selector-close {
  background: transparent;
  border: none;
  color: var(--mc-text-secondary);
  font-size: 20px;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;
}

.group-selector-close:hover {
  background-color: var(--mc-bg-tertiary);
  color: var(--mc-text-primary);
}

.group-selector-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  min-height: 0;
}

.group-selector-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--mc-text-secondary);
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.group-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.group-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--mc-bg-card);
  border: none;
  border-bottom: 1px solid var(--mc-border-primary);
  border-radius: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  width: 100%;
  color: var(--mc-text-primary);
}

.group-item:last-child {
  border-bottom: none;
}

.group-item:hover {
  background: var(--mc-bg-tertiary);
}

.group-item-active {
  background: rgba(30, 58, 95, 0.15);
}

.group-item-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--mc-bg-header);
  border-radius: 8px;
  color: var(--mc-text-light);
  font-size: 20px;
  flex-shrink: 0;
}

.group-item-content {
  flex: 1;
  min-width: 0;
}

.group-item-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--mc-text-primary);
  margin-bottom: 4px;
}

.group-item-school {
  font-size: 0.875rem;
  color: var(--mc-text-secondary);
  margin-bottom: 4px;
}

.group-item-student {
  font-size: 0.875rem;
  color: var(--mc-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
}

.group-item-check {
  color: var(--mc-bg-header);
  font-size: 20px;
  flex-shrink: 0;
}

.messaging-header-btn {
  position: relative;
}

.messaging-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 18px;
  color: #fff;
  text-align: center;
  background: #dc3545;
  border-radius: 9px;
}

.messaging-badge--dot {
  min-width: 10px;
  width: 10px;
  height: 10px;
  padding: 0;
  top: 4px;
  right: 4px;
  border-radius: 50%;
}
</style>
